import { useState } from "react"
import { Head, router } from "@inertiajs/react"
import { Plus, Pencil, Trash2, Check, X, MapPin } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"

type Region = { id: number; name: string; venues_count: number }

function RegionRow({ region }: { region: Region }) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(region.name)

  function handleSave() {
    if (!name.trim() || name.trim() === region.name) { setEditing(false); setName(region.name); return }
    router.patch(`/admin/regions/${region.id}`, { region: { name: name.trim() } }, {
      onSuccess: () => setEditing(false),
    })
  }

  function handleDelete() {
    if (confirm(`Delete "${region.name}"? ${region.venues_count > 0 ? `This will clear the region from ${region.venues_count} venue${region.venues_count !== 1 ? "s" : ""}.` : ""}`)) {
      router.delete(`/admin/regions/${region.id}`)
    }
  }

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <MapPin className="h-4 w-4 shrink-0 text-ink-muted" />
      {editing ? (
        <>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") { setEditing(false); setName(region.name) } }}
            className="flex-1 rounded-md border border-hairline bg-page px-2 py-1 text-sm text-ink-body focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button onClick={handleSave} className="text-green-600 hover:text-green-700" aria-label="Save"><Check className="h-4 w-4" /></button>
          <button onClick={() => { setEditing(false); setName(region.name) }} className="text-ink-muted hover:text-ink-body" aria-label="Cancel"><X className="h-4 w-4" /></button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm text-ink-body">{region.name}</span>
          <span className="text-xs text-ink-muted">{region.venues_count} venue{region.venues_count !== 1 ? "s" : ""}</span>
          <button onClick={() => setEditing(true)} className="text-ink-muted hover:text-ink-body" aria-label={`Edit ${region.name}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={handleDelete} className="text-ink-muted hover:text-red-600" aria-label={`Delete ${region.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </>
      )}
    </li>
  )
}

function AddRegionForm() {
  const [name, setName] = useState("")
  const [open, setOpen] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    router.post("/admin/regions", { region: { name: name.trim() } }, {
      onSuccess: () => { setName(""); setOpen(false) },
    })
  }

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4 mr-1.5" />
        Add region
      </Button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2">
      <input
        autoFocus
        type="text"
        placeholder="Region name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); setName("") } }}
        className="rounded-md border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <Button type="submit" disabled={!name.trim()}>Add</Button>
      <button type="button" onClick={() => { setOpen(false); setName("") }} className="text-ink-muted hover:text-ink-body"><X className="h-4 w-4" /></button>
    </form>
  )
}

export default function AdminRegionsIndex({ regions }: { regions: Region[] }) {
  return (
    <>
      <Head title="Regions">
        <meta name="description" content="Manage venue regions." />
        <meta property="og:title" content="Regions" />
        <meta property="og:description" content="Manage venue regions." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Regions</h1>
              <p className="mt-1">{regions.length} {regions.length === 1 ? "region" : "regions"}. Regions group venues by geographic area (e.g. Central Coast, Hunter Valley).</p>
            </div>
            <AddRegionForm />
          </div>
        </div>
        {regions.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">No regions yet. Add one above.</p>
        ) : (
          <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {regions.map((region) => (
              <RegionRow key={region.id} region={region} />
            ))}
          </ul>
        )}
      </AdminShell>
    </>
  )
}
